import React from 'react';
import { Link } from 'react-router-dom';
import SEO from '../components/SEO';
import { useLanguage } from '../context/LanguageContext';
import { company } from '../config/company';
import s from '../styles/legalStyles';

const PrivacyPolicy = () => {
  const { language } = useLanguage();
  const isNL = language === 'nl';

  return (
    <div style={s.page}>
      <SEO
        title={isNL ? 'Privacybeleid' : 'Privacy Policy'}
        description={isNL
          ? `Privacybeleid van ${company.brand}. Lees hoe wij persoonsgegevens verwerken volgens de AVG/GDPR.`
          : `Privacy policy of ${company.brand}. Read how we process personal data under GDPR.`}
      />
      <div style={s.container}>
        <h1 style={s.title}>{isNL ? 'Privacybeleid' : 'Privacy Policy'}</h1>
        <p style={s.lastUpdated}>
          {isNL ? 'Laatst bijgewerkt:' : 'Last updated:'} {company.effectiveDate}
        </p>

        {isNL ? (
          <article style={s.article}>
            <p>
              {company.brand} (handelend onder {company.name}, hierna "wij", "ons" of "{company.brand}")
              hecht veel waarde aan de bescherming van uw persoonsgegevens. In dit privacybeleid leggen wij uit hoe
              wij omgaan met persoonsgegevens die wij via onze website {company.website} verzamelen.
            </p>

            <h2 style={s.h2}>1. Verwerkingsverantwoordelijke</h2>
            <p>
              {company.name}<br />
              {company.street}<br />
              {company.postalCity}<br />
              {company.country}<br />
              KvK: {company.kvk}<br />
              E-mail: <a style={s.link} href={`mailto:${company.email}`}>{company.email}</a>
            </p>

            <h2 style={s.h2}>2. Welke persoonsgegevens verzamelen wij?</h2>
            <p>Wij verzamelen alleen gegevens die noodzakelijk zijn voor de werking van onze website en dienstverlening:</p>
            <ul style={s.list}>
              <li><strong>Contactgegevens:</strong> als u via WhatsApp of e-mail contact opneemt (naam, telefoon, e-mailadres).</li>
              <li><strong>Nieuwsbrief- en gids-aanvragen:</strong> als u een gratis gids (zoals <em>De Complete Koopplanner Spanje</em>) aanvraagt of zich inschrijft voor onze marketing-mails, verwerken wij uw naam en e-mailadres. Wij versturen periodiek onze <strong>nieuwste woning-listings</strong>, praktische tips over kopen in Spanje, en updates over CasaOthers. U kunt zich altijd met één klik onderaan elke e-mail uitschrijven.</li>
              <li><strong>Surfgedrag:</strong> via Google Analytics 4 verzamelen wij geanonimiseerde statistieken over paginabezoeken.</li>
              <li><strong>Technische gegevens:</strong> IP-adres, browsertype, apparaattype, taalvoorkeur.</li>
              <li><strong>Click-tracking:</strong> wanneer u op &quot;Bekijk woning&quot; klikt, registreren wij anoniem welke woning werd aangeklikt.</li>
            </ul>

            <h2 style={s.h2}>3. Doeleinden en grondslagen</h2>
            <p>Wij verwerken uw gegevens voor de volgende doeleinden:</p>
            <ul style={s.list}>
              <li><strong>Dienstverlening:</strong> u doorverwijzen naar onze partner Nelemans (gerechtvaardigd belang).</li>
              <li><strong>Website-optimalisatie:</strong> begrijpen welke woningen en pagina&apos;s populair zijn (toestemming via cookies).</li>
              <li><strong>Communicatie:</strong> reageren op uw vragen via WhatsApp of e-mail (uitvoering overeenkomst / toestemming).</li>
              <li><strong>Gratis gidsen &amp; nieuwsbrief:</strong> uw gedownloade gids afleveren en, indien u daarvoor expliciete toestemming heeft gegeven, periodiek onze nieuwste woning-listings en tips versturen (grondslag: toestemming — u kunt deze op elk moment intrekken).</li>
              <li><strong>Wettelijke verplichtingen:</strong> bewaarplichten op grond van Nederlandse en EU-wetgeving.</li>
            </ul>

            <h2 style={s.h2}>4. Doorgifte aan derden</h2>
            <p>Wij delen uw gegevens uitsluitend met:</p>
            <ul style={s.list}>
              <li><strong>Nelemans</strong>: onze vastgoedpartner in Spanje (alleen als u zelf doorklikt naar hun website).</li>
              <li><strong>Resend</strong>: e-mail service provider (EU-gehost) — voor het versturen van gidsen en de nieuwsbrief.</li>
              <li><strong>Google LLC</strong>: voor Google Analytics 4 en Google Tag Manager (server-locatie EU).</li>
              <li><strong>Meta (WhatsApp)</strong>: als u via WhatsApp contact opneemt.</li>
              <li><strong>Hostingprovider</strong>: voor het hosten van deze website.</li>
            </ul>
            <p>Wij verkopen of verhuren uw gegevens nooit aan derden voor commerciële doeleinden.</p>

            <h2 style={s.h2}>5. Bewaartermijn</h2>
            <p>
              Wij bewaren persoonsgegevens niet langer dan noodzakelijk:
            </p>
            <ul style={s.list}>
              <li>Contactverzoeken: maximaal 2 jaar na laatste contact.</li>
              <li>Nieuwsbrief-inschrijvingen: tot u zich uitschrijft. Onmiddellijk daarna wordt uw adres uit onze mail-lijst verwijderd.</li>
              <li>Gids-downloaders (zonder nieuwsbrief-inschrijving): maximaal 12 maanden.</li>
              <li>Analytische data: maximaal 14 maanden in Google Analytics.</li>
              <li>Click-tracking: maximaal 12 maanden, daarna geanonimiseerd.</li>
            </ul>

            <h2 style={s.h2}>6. Uw rechten (AVG)</h2>
            <p>U heeft het recht om:</p>
            <ul style={s.list}>
              <li>Inzage te krijgen in uw gegevens.</li>
              <li>Uw gegevens te laten corrigeren of verwijderen.</li>
              <li>Bezwaar te maken tegen de verwerking.</li>
              <li>Uw toestemming voor cookies in te trekken.</li>
              <li>Een klacht in te dienen bij de <a style={s.link} href="https://autoriteitpersoonsgegevens.nl" target="_blank" rel="noopener noreferrer">Autoriteit Persoonsgegevens</a>.</li>
            </ul>
            <p>
              Stuur uw verzoek naar{' '}
              <a style={s.link} href={`mailto:${company.email}`}>{company.email}</a>. Wij reageren binnen 30 dagen.
            </p>

            <h2 style={s.h2}>7. Beveiliging</h2>
            <p>
              Wij nemen passende technische en organisatorische maatregelen om uw gegevens te beveiligen, waaronder
              HTTPS-encryptie, beveiligde wachtwoorden (bcrypt), en beperkte toegang tot data.
            </p>

            <h2 style={s.h2}>8. Wijzigingen</h2>
            <p>
              Wij kunnen dit privacybeleid op elk moment aanpassen. De meest actuele versie staat altijd op deze pagina,
              met de datum bovenaan.
            </p>

            <h2 style={s.h2}>9. Contact</h2>
            <p>
              Vragen over privacy? Mail ons op{' '}
              <a style={s.link} href={`mailto:${company.email}`}>{company.email}</a> of stuur een{' '}
              <a style={s.link} href={company.whatsappUrl} target="_blank" rel="noopener noreferrer">WhatsApp bericht</a>.
            </p>

            <p style={s.footnote}>
              Zie ook ons <Link style={s.link} to="/cookies">Cookiebeleid</Link> en de{' '}
              <Link style={s.link} to="/voorwaarden">Algemene Voorwaarden</Link>.
            </p>
          </article>
        ) : (
          <article style={s.article}>
            <p>
              {company.brand} (trading as {company.name}, hereafter "we", "us" or "{company.brand}") values the
              protection of your personal data. In this privacy policy we explain how we handle personal data we
              collect through our website {company.website}.
            </p>

            <h2 style={s.h2}>1. Data controller</h2>
            <p>
              {company.name}<br />
              {company.street}<br />
              {company.postalCity}<br />
              {company.country}<br />
              Chamber of Commerce: {company.kvk}<br />
              Email: <a style={s.link} href={`mailto:${company.email}`}>{company.email}</a>
            </p>

            <h2 style={s.h2}>2. What personal data do we collect?</h2>
            <p>We only collect data necessary for the operation of our website and services:</p>
            <ul style={s.list}>
              <li><strong>Contact details:</strong> if you contact us via WhatsApp or email (name, phone, email address).</li>
              <li><strong>Newsletter &amp; guide requests:</strong> if you request a free guide (such as <em>The Complete Buyer&apos;s Planner Spain</em>) or subscribe to our marketing emails, we process your name and email. We periodically send our <strong>latest property listings</strong>, practical tips about buying in Spain, and CasaOthers updates. You can unsubscribe with one click at the bottom of every email.</li>
              <li><strong>Browsing behaviour:</strong> via Google Analytics 4 we collect anonymised statistics on page visits.</li>
              <li><strong>Technical data:</strong> IP address, browser type, device type, language preference.</li>
              <li><strong>Click tracking:</strong> when you click &quot;View property&quot;, we record anonymously which property was clicked.</li>
            </ul>

            <h2 style={s.h2}>3. Purposes and legal grounds</h2>
            <p>We process your data for the following purposes:</p>
            <ul style={s.list}>
              <li><strong>Service delivery:</strong> referring you to our partner Nelemans (legitimate interest).</li>
              <li><strong>Website optimisation:</strong> understanding which properties and pages are popular (consent via cookies).</li>
              <li><strong>Communication:</strong> responding to your questions via WhatsApp or email (contract performance / consent).</li>
              <li><strong>Free guides &amp; newsletter:</strong> delivering your requested guide and, if you have explicitly consented, sending you our latest property listings and tips (basis: consent — you may withdraw at any time).</li>
              <li><strong>Legal obligations:</strong> retention duties under Dutch and EU legislation.</li>
            </ul>

            <h2 style={s.h2}>4. Transfer to third parties</h2>
            <p>We share your data only with:</p>
            <ul style={s.list}>
              <li><strong>Nelemans</strong>: our real estate partner in Spain (only if you click through to their website).</li>
              <li><strong>Resend</strong>: our email service provider (EU-hosted) for sending guides and the newsletter.</li>
              <li><strong>Google LLC</strong>: for Google Analytics 4 and Google Tag Manager (server location EU).</li>
              <li><strong>Meta (WhatsApp)</strong>: if you contact us via WhatsApp.</li>
              <li><strong>Hosting provider</strong>: for hosting this website.</li>
            </ul>
            <p>We never sell or rent your data to third parties for commercial purposes.</p>

            <h2 style={s.h2}>5. Retention period</h2>
            <p>We do not retain personal data longer than necessary:</p>
            <ul style={s.list}>
              <li>Contact requests: maximum 2 years after last contact.</li>
              <li>Analytics data: maximum 14 months in Google Analytics.</li>
              <li>Click tracking: maximum 12 months, then anonymised.</li>
            </ul>

            <h2 style={s.h2}>6. Your rights (GDPR)</h2>
            <p>You have the right to:</p>
            <ul style={s.list}>
              <li>Access your data.</li>
              <li>Have your data corrected or deleted.</li>
              <li>Object to processing.</li>
              <li>Withdraw your consent for cookies.</li>
              <li>File a complaint with the <a style={s.link} href="https://autoriteitpersoonsgegevens.nl" target="_blank" rel="noopener noreferrer">Dutch Data Protection Authority</a>.</li>
            </ul>
            <p>
              Send your request to <a style={s.link} href={`mailto:${company.email}`}>{company.email}</a>. We respond
              within 30 days.
            </p>

            <h2 style={s.h2}>7. Security</h2>
            <p>
              We take appropriate technical and organisational measures to secure your data, including HTTPS encryption,
              secure password hashing (bcrypt), and restricted data access.
            </p>

            <h2 style={s.h2}>8. Changes</h2>
            <p>
              We may update this privacy policy at any time. The most current version is always on this page, with the
              date at the top.
            </p>

            <h2 style={s.h2}>9. Contact</h2>
            <p>
              Privacy questions? Email us at <a style={s.link} href={`mailto:${company.email}`}>{company.email}</a> or
              send a <a style={s.link} href={company.whatsappUrl} target="_blank" rel="noopener noreferrer">WhatsApp message</a>.
            </p>

            <p style={s.footnote}>
              See also our <Link style={s.link} to="/cookies">Cookies Policy</Link> and{' '}
              <Link style={s.link} to="/voorwaarden">Terms & Conditions</Link>.
            </p>
          </article>
        )}
      </div>
    </div>
  );
};

export default PrivacyPolicy;
